import axios from "axios";
import { API_BASE_URL, API_URL, NODE_API_BASE_URL } from "../../config";
import { authHeaders } from "../utils/session";

const getAuthHeaders = () => ({
  ...authHeaders(),
});

// ==================== USER PROFILE SERVICES ====================
/**
 * Fetch user lists
 */
export const fetchUserList = async (page = 1, filterData = {}) => {
  try {
    const res = await axios.get(`${NODE_API_BASE_URL}/api/subscription/get-customer-list-v3`, {
      params: { page, limit: 10, ...filterData },
      headers: getAuthHeaders(),
    });

    const payload = res?.data;

    return {
      data: Array.isArray(payload?.data) ? payload.data : [],
      total: Number(payload?.total ?? 0),
      lastPage: payload?.last_page ?? 1,
    };
  } catch (err) {
    throw new Error(err?.response?.data?.message || err?.message || "Failed to fetch user list");
  }
};

 

/**
 * Fetch user details
 */
export const fetchUserDetails = async (userId) => {
  const response = await axios.get(`${API_BASE_URL}/get-user-details`, {
    params: { user_id: userId },
    headers: getAuthHeaders(),
  });

  if (response.data.status === 200) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to fetch user details");
};

/**
 * Fetch user subscription details
 * Returns { user, subscription_plan, attorney_numbers, subscription_logs }
 */
export const fetchSubscriptionDetails = async (userId) => {
  const response = await axios.get(`${API_BASE_URL}/subscription/get-subscription-details`, {
    params: { user_id: userId },
    headers: getAuthHeaders(),
  });

  if (response.data.success === true) {
    return response.data.data;
  }
  throw new Error(
    response.data.message || "Failed to fetch subscription details",
  );
};

/**
 * Fetch attorney numbers and TM counts
 */
export const fetchAttorneyDetails = async (userId) => {
  const response = await axios.get(`${API_BASE_URL}/get-attorney-details`, {
    params: { user_id: userId },
    headers: getAuthHeaders(),
  });

  if (response.data.status === 200) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to fetch attorney details");
};

/**
 * Fetch features
 */
export const fetchFeatures = async () => {
  const response = await axios.get(`${API_BASE_URL}/get-features`, {
    headers: getAuthHeaders(),
  });
  
  
  if (response.status === 200) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to fetch plan features");
};

/**
 * Fetch API calls history with pagination
 */
export const fetchApiCalls = async ({ userId, page = 1, limit = 10 }) => {
  const response = await axios.get(`${API_BASE_URL}/get-user-api-calls`, {
    params: { user_id: userId, page, limit },
    headers: getAuthHeaders(),
  });

  if (response.data.status === 200) {
    return {
      data: response.data.data,
      total: response.data.total_data,
      currentPage: page,
      totalPages: Math.ceil(response.data.total_data / limit),
    };
  }
  throw new Error(response.data.message || "Failed to fetch API calls");
};

/**
 * Fetch wallet transactions with pagination
 */
export const fetchWalletTransactions = async ({
  userId,
  page = 1,
  limit = 10,
}) => {
  const response = await axios.get(`${API_BASE_URL}/get-wallet-details`, {
    params: { user_id: userId, page, limit },
    headers: getAuthHeaders(),
  });

  if (response.data.status === 200) {
    return {
      data: response.data.details,
      total: response.data.total,
      balance: response.data.balance,
      currentPage: page,
      totalPages: Math.ceil(response.data.total / limit),
    };
  }
  throw new Error(
    response.data.message || "Failed to fetch wallet transactions",
  );
};

// ==================== UPDATE SERVICES ====================

/**
 * Update user details
 */
export const updateUserDetails = async ({ userId, data }) => {
  const response = await axios.post(
    `${API_BASE_URL}/update-user-details`,
    { user_id: userId, ...data },
    { headers: getAuthHeaders() },
  );

  if (response.data.success !== true) {
    throw new Error(response.data.message || "Failed to update user details");
  }

  return response.data;
};

/**
 * Update subscription plan
 */
export const updateSubscriptionPlan = async ({ userId, planData }) => {
  planData.append("user_id", userId);

  let response;
  try {
    response = await axios.post(
      `${API_BASE_URL}/subscription/update-user-subscription`,
      planData,
      { headers: getAuthHeaders() },
    );
  } catch (err) {
    // The API returns its reason in the body even on non-2xx, e.g.
    // { success: false, message: "No active subscription found for this user." }
    // Axios only exposes "Request failed with status code NNN" on err.message,
    // so read the body first.
    const data = err?.response?.data;
    throw new Error(
      data?.message || data?.error || err?.message || "Failed to update subscription",
    );
  }

  if (response.data.success === true || response.data.pending_approval === true || response.data.online_payment === true) {
    return response.data;
  }
  throw new Error(response.data.message || "Failed to update subscription");
};



/**
 * Save/Update updation requirement
 */
export const saveUpdationRequirement = async ({ userId, formData }) => {
  formData.append("user_id", userId);

  const response = await axios.put(
    `${API_BASE_URL}/update-updation-requirement`,
    formData,
    { headers: getAuthHeaders() }
  );

  if (response.data.status === 200) {
    return response.data.data;
  }
  throw new Error(
    response.data.message || "Failed to save updation requirement"
  );
};



/**
 * Fetch subscription history for a user
 */
export const fetchSubscriptionHistory = async (userId) => {
  const response = await axios.get(
    `${API_BASE_URL}/get-subscription-history`,
    {
      params: { user_id: userId },
      headers: getAuthHeaders(),
    }
  );

  if (response.data.status === 200) {
    return response.data.data;
  }
  throw new Error(
    response.data.message || "Failed to fetch subscription history"
  );
};


/**
 * Update plan features
 */
export const updatePlanFeatures = async ({ userId, features }) => {
  const response = await axios.put(
    `${API_BASE_URL}/update-plan-features`,
    { user_id: userId, features },
    { headers: getAuthHeaders() },
  );

  if (response.data.status === 200) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to update features");
};

/**
 * Enable / disable a single feature for a user.
 *
 * Endpoint : POST /subscription/update-user-feature
 * Payload  : { user_id, feature_id, action: "enable" | "disable" }
 *
 * The backend updates user_subscription_plan.features and echoes the new list
 * back as data.feature_list / data.features, which the caller can use directly
 * instead of refetching get-subscription-details.
 */
export const updateUserFeature = async ({ userId, featureId, action }) => {
  let response;
  try {
    response = await axios.post(
      `${API_BASE_URL}/subscription/update-user-feature`,
      { user_id: userId, feature_id: featureId, action },
      { headers: getAuthHeaders() },
    );
  } catch (err) {
    // Surface the API's own message rather than "Request failed with status NNN"
    const data = err?.response?.data;
    throw new Error(
      data?.message || data?.error || err?.message || `Failed to ${action} feature`,
    );
  }

  const body = response.data || {};
  const ok = body.success === true || body.status === 200 || body.status === true;
  if (!ok) {
    throw new Error(body.message || `Failed to ${action} feature`);
  }

  // Normalize whichever shape the API returns into a comma-separated id string
  const raw = body.data?.feature_list ?? body.data?.features ?? null;
  const features = Array.isArray(raw) ? raw.join(",") : raw;

  return { ...body, features };
};

/**
 * Add new attorney numbers
 * Payload: { user_id, attorney_numbers: [{ number, tm_count }] }
 */
export const addAttorneyNumbers = async ({ userId, attorneyNumbers }) => {
  const response = await axios.post(
    `${API_BASE_URL}/subscription/add-attorney-numbers`,
    { user_id: userId, attorney_numbers: attorneyNumbers },
    { headers: getAuthHeaders() },
  );

  if (response.data.status === 200 || response.data.success) {
    return response.data;
  }
  throw new Error(response.data.message || "Failed to add attorney numbers");
};

/**
 * Remove an attorney number from a customer.
 *
 * Permanent, and it takes the monitored trademarks and proprietors under that
 * number with it — which is why the caller confirms first.
 *
 * The response carries the remaining list and the new total, so nothing has to
 * be re-fetched to show the result.
 *
 * @param {{userId: number|string, attorneyNumber: string}} args
 * @returns {{message: string, deleted: string[], not_found: string[],
 *   total_tm_count: number, data: Array}}
 */
export const deleteAttorneyNumber = async ({ userId, attorneyNumber }) => {
  try {
    const res = await axios.post(
      `${API_BASE_URL}/subscription/delete-attorney-number`,
      { user_id: userId, attorney_number: String(attorneyNumber) },
      { headers: getAuthHeaders() },
    );
    const body = res.data || {};
    /* Some endpoints here answer 200 with the failure in the body, so success
       is confirmed rather than assumed. */
    if (body.success === false || (body.status && Number(body.status) >= 400)) {
      const e = new Error(body.message || "The attorney number could not be deleted");
      e.status = Number(body.status) || 400;
      throw e;
    }
    return body;
  } catch (err) {
    if (err.status) throw err;
    /* The API explains itself in the body; axios's own "Request failed with
       status code 404" tells the reader nothing they can act on. */
    const body = err?.response?.data;
    const e = new Error(
      body?.message || body?.msg || "The attorney number could not be deleted",
    );
    e.status = err?.response?.status;
    throw e;
  }
};

/**
 * Update attorney numbers
 */
export const updateAttorneyNumbers = async ({ userId, attorneyNumbers }) => {
  const response = await axios.put(
    `${API_BASE_URL}/update-attorney-numbers`,
    { user_id: userId, attorney_numbers: attorneyNumbers },
    { headers: getAuthHeaders() },
  );

  if (response.data.status === 200) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to update attorney numbers");
};


/**
 * Update user status (activate/deactivate)
 */
export const updateUserStatus = async ({ userId, status }) => {
  const response = await axios.post(
    `${API_BASE_URL}/manage-user-status?status=${status}&user_id=${userId}`,
    { user_id: String(userId), status },
    { headers: getAuthHeaders() },
  );

  if (response.data.status === 200) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to update status");
};
